// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { ExistingAsset } from '@/api/types';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormTextField } from '@/components/form-text-field';
import { OpenInNewRounded } from '@/constants/hardfin-standard-icons';
import { useSupportError } from '@/hooks/use-support-error';
import { QuarantinedSerial, Shipment } from '@/shipment/api/types';
import { QuarantineResolutionDialogShell } from '@/shipment/components/quarantine-resolution-dialog-shell';
import { QuarantineResolutionPreviewRow } from '@/shipment/components/quarantine-resolution-preview-row';
import { useResolveQuarantinedSerial } from '@/shipment/hooks/use-resolve-quarantined-serial';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Divider, Link, Stack, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

// Only the corrected-serial kind collects a serial; the other two create the
// asset under the serial the 3PL reported, so the field stays unvalidated.
function buildFormSchema(isSerialEditable: boolean) {
  return z.object({
    note: z.string().min(1, 'A note is required to resolve a held serial'),
    correctedSerial: isSerialEditable
      ? z.string().trim().min(1, 'A corrected serial is required')
      : z.string(),
  });
}

type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

// The kinds that bring a new unit into existence. They differ in why the serial
// was held, and in whether the serial is confirmed as reported or retyped.
type AssetCreatingResolutionKind =
  | 'CREATED_ASSET'
  | 'ACCEPTED_SERIAL'
  | 'CORRECTED_SERIAL';

interface Props {
  isOpen: boolean;
  shipmentKey: string;
  quarantinedSerial: QuarantinedSerial;
  kind: AssetCreatingResolutionKind;
  // Where the asset comes into being: the shipment origin for a ship or a
  // return, the receive location for a purchase order.
  creationLocation: Shipment['fromLocation'];
  // The created asset's inventory date: when the shipment shipped, or when it
  // was received for an inbound hold.
  inventoryDate: DateTime | null;
  onClose: () => void;
  // Offered when the serial turns out to be taken: swaps this dialog for the
  // one that changes the serial. Absent on the dialog that already does that.
  onCorrectSerialInstead?: () => void;
}

interface Copy {
  title: string;
  reason: string;
  placeholder: string;
  submitLabel: string;
}

const PATTERN_MISMATCH_REASON =
  "This serial does not match your organization's configured serial patterns.";

const UNKNOWN_SERIAL_REASON = 'Hardfin has no record of this serial.';

/** Says why the hold exists, blaming the pattern list only when it is to blame. */
function toHoldReason(
  quarantineType: QuarantinedSerial['quarantineType']
): string {
  if (quarantineType === 'MISSING_SERIAL') {
    return '';
  }
  return quarantineType === 'SERIAL_PATTERN_MISMATCH'
    ? PATTERN_MISMATCH_REASON
    : UNKNOWN_SERIAL_REASON;
}

function toDialogCopy(
  kind: AssetCreatingResolutionKind,
  quarantineType: QuarantinedSerial['quarantineType']
): Copy {
  if (kind === 'CORRECTED_SERIAL') {
    // A unit reported with no serial has none to offer an alternate to.
    const isMissing = quarantineType === 'MISSING_SERIAL';
    const label = isMissing ? 'Specify new serial' : 'Specify alternate serial';
    return {
      title: label,
      reason: toHoldReason(quarantineType),
      placeholder: isMissing
        ? 'Where does the new serial come from?'
        : 'Where does the alternate serial come from?',
      submitLabel: label,
    };
  }
  if (kind === 'ACCEPTED_SERIAL') {
    return {
      title: 'Accept serial',
      reason: toHoldReason(quarantineType),
      placeholder: 'Why is this serial correct as reported?',
      submitLabel: 'Accept serial',
    };
  }
  return {
    title: 'Create new asset',
    reason: 'Hardfin has no record of this serial.',
    placeholder: 'Why is this device new to Hardfin?',
    submitLabel: 'Create asset',
  };
}

/**
 * Resolution dialog for the two kinds that create the reported unit: a serial
 * Hardfin has no record of, and a serial that matches none of the
 * organization's patterns but is correct as reported. Nothing in the preview is
 * editable — the asset mirrors the 3PL's report.
 */
export function QuarantineCreateAssetDialog(props: Props) {
  const { showSupportError, hideSupportError } = useSupportError();
  const { user } = useCurrentUser();
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
  // The serial-exists refusal has a way out: correcting the serial. It is
  // tracked separately because the offer is a link, not curated copy.
  // The asset already carrying the serial, so the refusal can link to it.
  const [takenAsset, setTakenAsset] = useState<ExistingAsset | null>(null);
  const resolveMutation = useResolveQuarantinedSerial(props.shipmentKey);

  const isSerialEditable = props.kind === 'CORRECTED_SERIAL';
  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(buildFormSchema(isSerialEditable)),
    // A correction starts from what the 3PL reported, since it is usually a
    // character or two out rather than a different serial entirely.
    defaultValues: {
      note: '',
      correctedSerial: props.quarantinedSerial.serial,
    },
  });

  const reportedItem = props.quarantinedSerial.reportedItem;
  const copy = toDialogCopy(props.kind, props.quarantinedSerial.quarantineType);

  const close = (options?: { ignorePending?: boolean }) => {
    if (resolveMutation.isPending && !options?.ignorePending) {
      return;
    }
    props.onClose();
    reset();
    setRefusalMessage(null);
    setTakenAsset(null);
  };

  // The parent swaps dialogs rather than closing this one, so clear what the
  // refused attempt left behind before handing over.
  const handOffToCorrection = () => {
    reset();
    setRefusalMessage(null);
    setTakenAsset(null);
    props.onCorrectSerialInstead?.();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    hideSupportError();
    setRefusalMessage(null);
    setTakenAsset(null);
    resolveMutation.mutate(
      {
        quarantineKey: props.quarantinedSerial.key,
        kind: props.kind,
        note: data.note.trim(),
        correctedSerial: isSerialEditable
          ? data.correctedSerial.trim()
          : undefined,
      },
      {
        onSuccess: () => close({ ignorePending: true }),
        onError: (error) => {
          if (isPlanLimitError(error)) {
            return;
          }

          const apiError = handleAPIError(error);
          // A refusal (the serial now exists, a cancelled shipment) is the
          // shipment's own state rather than a fault, so it stays on a plain
          // Alert instead of the support path.
          const curated = getUserFacingErrorMessage(apiError);
          if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
            hideSupportError();
            setTakenAsset(
              apiError.errorType === 'quarantine_serial_exists'
                ? (apiError.existingAsset ?? null)
                : null
            );
            setRefusalMessage(curated);
            return;
          }
          setRefusalMessage(null);
          if (!apiError.sentToSentry) {
            captureException(error);
          }
          showSupportError(
            'An error occurred while resolving the held serial.'
          );
        },
      }
    );
  };

  return (
    <QuarantineResolutionDialogShell
      isOpen={props.isOpen}
      title={copy.title}
      submitLabel={copy.submitLabel}
      isPending={formState.isSubmitting || resolveMutation.isPending}
      onSubmit={handleSubmit(handleValidSubmission)}
      onClose={() => close()}
    >
      <Typography variant="body1" sx={{ mt: 2 }}>
        {props.quarantinedSerial.serial === '' ? (
          <>
            The 3PL reported an item <strong>{reportedItem.sku}</strong> (
            {reportedItem.name}) with no serial.
          </>
        ) : (
          <>
            The 3PL reported serial{' '}
            <strong>{props.quarantinedSerial.serial}</strong> as item{' '}
            <strong>{reportedItem.sku}</strong> ({reportedItem.name}).{' '}
            {copy.reason}
          </>
        )}
      </Typography>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Asset to be created
      </Typography>
      <Stack
        useFlexGap
        spacing={0.5}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
      >
        <QuarantineResolutionPreviewRow label="Item">
          {`${reportedItem.sku} (${reportedItem.name})`}
        </QuarantineResolutionPreviewRow>
        <QuarantineResolutionPreviewRow label="Initial location">
          {props.creationLocation?.name ?? '—'}
        </QuarantineResolutionPreviewRow>
        <QuarantineResolutionPreviewRow label="Inventory date">
          {
            // The backend dates the asset in the organization's timezone.
            props.inventoryDate
              ?.setZone(user.organization.timezone)
              .toLocaleString({ dateStyle: 'medium' }) ?? '—'
          }
        </QuarantineResolutionPreviewRow>
        {isSerialEditable && <Divider sx={{ my: 1 }} />}
        <QuarantineResolutionPreviewRow label="Serial">
          {isSerialEditable ? (
            <FormTextField
              control={control}
              name="correctedSerial"
              placeholder="Corrected serial"
            />
          ) : (
            props.quarantinedSerial.serial
          )}
        </QuarantineResolutionPreviewRow>
      </Stack>

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Note
      </Typography>
      <FormTextField
        control={control}
        name="note"
        fullWidth={true}
        isMultiline={true}
        minimumRows={2}
        placeholder={copy.placeholder}
      />

      {refusalMessage !== null && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {takenAsset !== null ? (
            <>
              This serial already exists as an asset in Hardfin. You can
              {props.onCorrectSerialInstead !== undefined ? ' either ' : ' '}
              <Link
                // A new tab loads the page fresh, needing no navigation.
                href={`/assets/${takenAsset.key}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  verticalAlign: 'baseline',
                }}
              >
                edit the existing asset
                <OpenInNewRounded sx={{ fontSize: 14 }} />
              </Link>{' '}
              to change its serial
              {props.onCorrectSerialInstead !== undefined ? (
                <>
                  , or{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={handOffToCorrection}
                    // A button sits off the sentence line by default.
                    sx={{ font: 'inherit', verticalAlign: 'baseline' }}
                  >
                    specify a new serial
                  </Link>{' '}
                  to import this new asset.
                </>
              ) : (
                '.'
              )}
            </>
          ) : (
            refusalMessage
          )}
        </Alert>
      )}
    </QuarantineResolutionDialogShell>
  );
}
